/**
 * Fluid typography — responsive v2, Phase 1.
 *
 * Makes Duro's type scale (font-size + line-height) scale smoothly between the
 * mobile and desktop viewports instead of staying at one fixed desktop size, so
 * a published page is correctly PROPORTIONED on a phone, not just re-stacked (a
 * 36px headline becomes ~26px on mobile, not a 36px shout). It reuses the exact
 * seam `brandToDuroVars` already uses — overriding Duro's `--fontSizeN` /
 * `--lineHeightN` custom properties in the injected brand `:root` block — so the
 * fix lands in BOTH the published render and the live editor canvas at once.
 *
 * ## The viewport basis — `--wb-vw` (NOT raw `vw`)
 *
 * The formula scales against `var(--wb-vw, 100vw)`, never a bare `vw` unit. In
 * the PUBLISHED page nothing sets `--wb-vw` (the scale is declared on `:root`),
 * so it falls back to `100vw` = the real viewport. In the EDITOR the canvas is
 * xyflow-zoom-scaled, so a bare `vw` would read the whole browser window and
 * preview the wrong size. The canvas instead pins `--wb-vw` to the active
 * authoring width (390 / 768 / capped-1440).
 *
 * CRUCIAL placement: because the clamp lives inside a custom property
 * (`--fontSizeN`), its nested `var(--wb-vw)` resolves at the element that
 * DECLARES `--fontSizeN` — NOT at the use site or a descendant. In the editor
 * that is each page body, which spreads `brandToDuroVars` through ThemeVars; it
 * inherits `--wb-vw` from the canvas scope (marked `data-wb-scope`), where
 * SitemapCanvas sets it on each breakpoint change. Setting it below the body
 * has no effect. All page nodes share one authoring width per breakpoint, so
 * one container-level value is correct.
 *
 * ## Auto defaults
 *
 * `max` = Duro's current desktop size (desktop output unchanged); `min` =
 * `max` compressed toward a comfortable reading base so LARGE sizes shrink most
 * and body text (≤ base) is left alone. Existing sites get good mobile type with
 * zero authoring work. Values mirror @duro-app 0.34.x; a Duro type-scale bump
 * would need the `*_REM` tables re-synced (like the rest of the Duro contract).
 */
// Barrel on purpose — see duroTheme.ts: the deep `tokens/*.css` subpaths ship
// uncompiled StyleX source that throws outside the babel pipeline, and this
// module is read as data from SSR/Node and vitest.
// eslint-disable-next-line duro/no-tokens-barrel-import
import { typeScale } from "@duro-app/tokens";

/** CSS var carrying the fluid viewport basis (px in the editor, `100vw` else). */
export const WB_VW_VAR = "--wb-vw";
/** Viewport endpoints the scale interpolates between (mobile → desktop). */
export const MOBILE_VW = 390;
export const DESKTOP_VW = 1440;
const SPAN = DESKTOP_VW - MOBILE_VW;

/** Comfortable reading base (rem): sizes at/below this don't compress. */
const BASE_REM = 1;
/** How hard sizes above the base shrink toward it on mobile (0..1). */
const COMPRESSION = 0.5;

// Duro's desktop type scale (rem), extracted from @duro-app/ui index.css. `max`
// anchors — the published desktop size stays exactly these.
const FONT_SIZE_REM: Record<number, number> = {
  1: 0.75,
  2: 0.8125,
  3: 0.875,
  4: 1,
  5: 1.125,
  6: 1.25,
  7: 1.5,
  8: 1.875,
  9: 2.25,
};
const LINE_HEIGHT_REM: Record<number, number> = {
  1: 1,
  2: 1.25,
  3: 1.25,
  4: 1.5,
  5: 1.5,
  6: 1.75,
  7: 2,
  8: 2.25,
  9: 2.75,
};

/** `"var(--fontSize9-xveysf7)"` → `"--fontSize9-xveysf7"`. */
function varName(ref: string): string {
  const m = /^var\((--[A-Za-z0-9-]+)\)$/.exec(ref);
  if (!m) throw new Error(`unexpected Duro token ref: ${ref}`);
  return m[1];
}

/** Trim float noise (4dp, no trailing zeros). */
const fmt = (n: number): string => String(Number(n.toFixed(4)));

/** The mobile (min) size for a desktop (max) size: shrink the part above the
 *  reading base by COMPRESSION, leaving body text untouched. */
export function compress(maxRem: number): number {
  return maxRem - Math.max(0, maxRem - BASE_REM) * COMPRESSION;
}

/** A `clamp()` that ramps `minRem → maxRem` across the mobile→desktop viewport,
 *  keyed on `--wb-vw`. When there's nothing to ramp (min === max) it stays a
 *  plain static size — no needless clamp on body text. Length-correct: the
 *  `(basis − 390px) / 1050px` ratio is unitless, so `× Δrem` yields rem. */
export function fluidRem(minRem: number, maxRem: number): string {
  if (minRem >= maxRem) return `${fmt(maxRem)}rem`;
  const delta = fmt(maxRem - minRem);
  const basis = `(var(${WB_VW_VAR}, 100vw) - ${MOBILE_VW}px) / ${SPAN}px`;
  return (
    `clamp(${fmt(minRem)}rem, ` +
    `calc(${fmt(minRem)}rem + ${delta}rem * ${basis}), ` +
    `${fmt(maxRem)}rem)`
  );
}

/**
 * The fluid overrides for Duro's type scale: `--fontSizeN` → fluid font-size and
 * `--lineHeightN` → the line-height scaled by the SAME ratio (so leading stays
 * proportional as the size shrinks). Brand-independent (Duro type sizes don't
 * vary by brand), so it's computed once and merged into `brandToDuroVars`.
 */
function buildFluidTypeVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 1; i <= 9; i++) {
    const fMax = FONT_SIZE_REM[i]!;
    const fMin = compress(fMax);
    out[
      varName(typeScale[`fontSize${i}` as keyof typeof typeScale] as string)
    ] = fluidRem(fMin, fMax);
    const lMax = LINE_HEIGHT_REM[i]!;
    const lMin = lMax * (fMin / fMax);
    out[
      varName(typeScale[`lineHeight${i}` as keyof typeof typeScale] as string)
    ] = fluidRem(lMin, lMax);
  }
  return out;
}

export const FLUID_TYPE_VARS: Readonly<Record<string, string>> =
  buildFluidTypeVars();
